/**
 * Typed API surface for the Prompt-Assistant write/read flows (Stories 8.3,
 * 8.4 and 8.3b). All functions wrap `apiClient` and surface `ApiError` on
 * non-2xx responses.
 *
 *   chat()           — POST /admin/prompts/:id/assistant/chat        (operator+)
 *   listSessions()   — GET  /admin/prompts/:id/assistant/sessions    (viewer+)
 *   getSession()     — GET  /admin/prompts/assistant/sessions/:id    (viewer+)
 *   preview()        — POST /admin/prompts/assistant/sessions/:id/preview (operator+)
 *   apply()          — POST /admin/prompts/assistant/sessions/:id/apply   (operator+/admin)
 *   discard()        — POST /admin/prompts/assistant/sessions/:id/discard (operator+)
 *
 * The shapes here mirror the backend TypeBox schemas in
 * `apps/api/src/routes/admin/prompts.schemas.ts` and
 * `apps/api/src/routes/admin/prompt-assistant.schemas.ts`. They are kept as
 * thin local copies so the admin bundle does not import from the api package.
 */

import { apiClient } from './api';

// ─────────────────────────────────────────────────────────────────────────────
// Shared types
// ─────────────────────────────────────────────────────────────────────────────

export interface ProposedChange {
  section: string;
  before: string;
  after: string;
  rationale: string;
}

export interface ProposedCodeChange {
  file: string;
  change: string;
}

export type AssistantSessionStatus = 'open' | 'applied' | 'discarded';

export interface AssistantSession {
  id: string;
  promptId: string;
  userDescription: string;
  referenceConversationCount: number;
  status: AssistantSessionStatus;
  appliedVersionId: string | null;
  appliedAt: string | null;
  startedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export type AssistantMessageRole = 'user' | 'assistant' | 'system';

export interface AssistantMessage {
  id: string;
  role: AssistantMessageRole;
  content: string;
  proposedChanges: ProposedChange[] | null;
  proposedCodeChanges: ProposedCodeChange[] | null;
  openaiTokensTotal: number | null;
  openaiLatencyMs: number | null;
  createdAt: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Chat (Story 8.3)
// ─────────────────────────────────────────────────────────────────────────────

export interface ChatTurnInput {
  promptId: string;
  sessionId: string | null;
  userMessage: string;
  referenceConversationIds: string[];
}

export interface ChatTurnResponse {
  sessionId: string;
  messageId: string;
  analysis: string;
  proposedChanges: ProposedChange[];
  proposedCodeChanges: ProposedCodeChange[];
  tokensTotal: number;
  latencyMs: number;
}

export async function chatTurn(input: ChatTurnInput): Promise<ChatTurnResponse> {
  return apiClient.post<ChatTurnResponse>(
    `/admin/prompts/${encodeURIComponent(input.promptId)}/assistant/chat`,
    {
      sessionId: input.sessionId,
      userMessage: input.userMessage,
      referenceConversationIds: input.referenceConversationIds,
    },
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Apply / Discard / Preview (Story 8.4)
// ─────────────────────────────────────────────────────────────────────────────

export interface PreviewResponse {
  sessionId: string;
  messageId: string;
  currentSystemPrompt: string;
  proposedSystemPrompt: string;
  proposedChanges: ProposedChange[];
}

export async function previewSession(sessionId: string): Promise<PreviewResponse> {
  return apiClient.post<PreviewResponse>(
    `/admin/prompts/assistant/sessions/${encodeURIComponent(sessionId)}/preview`,
  );
}

export interface ApplyInput {
  sessionId: string;
  activate: boolean;
  changeSummary?: string | null;
}

export interface ApplyResponse {
  sessionId: string;
  versionId: string;
  versionNumber: number;
  activated: boolean;
  /**
   * Story 8.5b — populated when `activated:false` despite `activate:true` was
   * requested. UI surfaces this as a `toast.warning` so the operator knows
   * WHY activation didn't happen (e.g. role restriction). Null on success or
   * when activate was never requested.
   */
  activationFailureReason: string | null;
}

export async function applySession(input: ApplyInput): Promise<ApplyResponse> {
  return apiClient.post<ApplyResponse>(
    `/admin/prompts/assistant/sessions/${encodeURIComponent(input.sessionId)}/apply`,
    { activate: input.activate, changeSummary: input.changeSummary ?? null },
  );
}

export interface DiscardResponse {
  sessionId: string;
  status: 'discarded';
}

export async function discardSession(sessionId: string): Promise<DiscardResponse> {
  return apiClient.post<DiscardResponse>(
    `/admin/prompts/assistant/sessions/${encodeURIComponent(sessionId)}/discard`,
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Read endpoints (Story 8.3b)
// ─────────────────────────────────────────────────────────────────────────────

export interface ListSessionsInput {
  promptId: string;
  page?: number;
  pageSize?: number;
  status?: AssistantSessionStatus;
}

export interface ListSessionsResponse {
  data: AssistantSession[];
  total: number;
  page: number;
  pageSize: number;
}

export async function listSessions(input: ListSessionsInput): Promise<ListSessionsResponse> {
  const params = new URLSearchParams();
  if (input.page !== undefined) params.set('page', String(input.page));
  if (input.pageSize !== undefined) params.set('pageSize', String(input.pageSize));
  if (input.status) params.set('status', input.status);
  const qs = params.toString();
  const url = `/admin/prompts/${encodeURIComponent(input.promptId)}/assistant/sessions${qs ? `?${qs}` : ''}`;
  return apiClient.get<ListSessionsResponse>(url);
}

export interface SessionDetailResponse {
  session: AssistantSession;
  messages: AssistantMessage[];
}

export async function getSessionDetail(sessionId: string): Promise<SessionDetailResponse> {
  return apiClient.get<SessionDetailResponse>(
    `/admin/prompts/assistant/sessions/${encodeURIComponent(sessionId)}`,
  );
}
